import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { furnitureService, FurnitureData } from '../../services/furniture';
import { useTranslation } from 'react-i18next';

const SellerDashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [myListings, setMyListings] = useState<FurnitureData[]>([]);
  const [stats, setStats] = useState({
    totalListings: 0,
    activeListing: 0
  });
  const [loading, setLoading] = useState(true);

  const fetchSellerData = async () => {
    try {
      setLoading(true);
      // Fetch all of the seller's listings (backend statuses: available | pending | sold | removed)
      const response = await furnitureService.getSellerListings(undefined, 1, 20);
      const listings: FurnitureData[] = response.furniture || [];
      setMyListings(listings);

      // Calculate stats from actual data
      setStats({
        totalListings: response.total ?? listings.length,
        activeListing: listings.filter((item) => item.status === 'available').length
      });
    } catch (error) {
      console.error('Error fetching seller data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (itemId: string) => {
    navigate(`/edit-furniture/${itemId}`);
  };

  const handleMarkSold = async (itemId: string) => {
    try {
      await furnitureService.updateFurniture(itemId, { status: 'sold' });
      await fetchSellerData();
    } catch (error) {
      console.error('Error marking furniture as sold:', error);
      alert(t('seller.dashboard.markSoldFailed'));
    }
  };

  const handleDelete = async (itemId: string, itemTitle: string) => {
    if (window.confirm(t('seller.dashboard.confirmDelete', { title: itemTitle }))) {
      try {
        await furnitureService.deleteFurniture(itemId);
        // Refresh the listings after deletion
        await fetchSellerData();
        alert(t('seller.dashboard.deleted'));
      } catch (error) {
        console.error('Error deleting furniture:', error);
        alert(t('seller.dashboard.deleteFailed'));
      }
    }
  };

  useEffect(() => {
    fetchSellerData();
  }, [location.pathname]); // Refresh when returning to dashboard

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('seller.dashboard.myListings')}</h1>
          <p className="text-gray-600 mt-2">{t('seller.dashboard.subtitle')}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{t('seller.dashboard.totalListings')}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalListings}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="bg-green-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{t('seller.dashboard.activeListings')}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeListing}</p>
              </div>
            </div>
          </div>

        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Link
            to="/sell"
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold">{t('seller.dashboard.addListing')}</h3>
                <p className="text-blue-100">{t('seller.dashboard.addListingDesc')}</p>
              </div>
            </div>
          </Link>

          <Link to="/buyer/messages" className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="bg-orange-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('navbar.messages')}</h3>
                <p className="text-gray-600">{t('seller.dashboard.messagesDesc')}</p>
              </div>
            </div>
          </Link>

        </div>

        {/* My Listings */}
        <div>
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-gray-900">{t('seller.dashboard.myListings')}</h2>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow animate-pulse">
                  <div className="h-48 bg-gray-300 rounded-t-lg"></div>
                  <div className="p-4">
                    <div className="h-4 bg-gray-300 rounded mb-2"></div>
                    <div className="h-4 bg-gray-300 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : myListings.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-gray-100 rounded-lg p-8">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('seller.dashboard.noListings')}</h3>
                <p className="mt-1 text-sm text-gray-500">{t('seller.dashboard.noListingsDesc')}</p>
                <div className="mt-6">
                  <Link
                    to="/sell"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {t('seller.dashboard.addListing')}
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {myListings.map((item) => (
                <div key={item._id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                  {item.images && item.images.length > 0 ? (
                    <img
                      src={item.images[0]}
                      alt={item.title}
                      className="w-full h-48 object-cover rounded-t-lg"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-200 rounded-t-lg flex items-center justify-center">
                      <svg className="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-600 text-sm mb-2">{item.description.substring(0, 60)}...</p>
                    {item.status === 'removed_by_admin' && item.moderation?.reason && (
                      <p className="mb-2 text-sm text-red-700">{t('seller.dashboard.moderationReason')}: {item.moderation.reason}</p>
                    )}
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-lg font-bold text-green-600">${item.price}</span>
                      <span className="text-sm text-gray-500 capitalize">{t(`furniture.condition.${item.condition}`)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                          item.status === 'available'
                            ? 'bg-green-100 text-green-800'
                            : item.status === 'sold'
                            ? 'bg-gray-200 text-gray-700'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {t(`seller.dashboard.status.${item.status || 'available'}`)}
                      </span>
                      {item.status !== 'removed_by_admin' && <div className="flex space-x-2">
                        {item.status === 'available' && <button
                          onClick={() => handleMarkSold(item._id)}
                          className="text-gray-600 hover:text-gray-800 text-sm font-medium hover:underline"
                        >
                          {t('seller.dashboard.markSold')}
                        </button>}
                        <button
                          onClick={() => handleEdit(item._id)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline"
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(item._id, item.title)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium hover:underline"
                        >
                          {t('common.delete')}
                        </button>
                      </div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerDashboard;
